import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { StyledH2, StyledModal } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";

import { store } from "@components/VerticalLayout/store";
import {
    setIds,
    setSelectedRow,
    store as dataStore,
    setKey,
    setShopDataIsUpdated,
    setIsLoading,
} from "../store";
import { uuid } from "uuidv4";
import ErrorToast from "@components/Common/ErrorTost";

import { CustomIconsAssociateModal } from "./CustomIconsAssociateModal";
import { ConfirmationMessage } from "./ConfirmationMessage";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import { useAuthHeader } from "../../../../api/headersKeycloak";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";

type SaleSupportAssociateModalType = {
    isAssociateModalOpened: boolean;
    setIsAssociateModalOpened: Function;
    selectedShopsRows: any;
};

export function SaleSupportAssociateModal({
    setIsAssociateModalOpened,
    isAssociateModalOpened,
    selectedShopsRows,
}: SaleSupportAssociateModalType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const getAuthHeader = useAuthHeader();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const {
        idsLocalShop,
        activeSaleSupports,
        shopsSelectedRow,
        localShop,
        associatedSaleSupport,
        shopDataIsUpdated,
        salesSupportFavored,
    } = useSnapshot(dataStore);

    const shopId = 0;
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = localStorage.getItem("uuidUser");
    const [isValidateClicked, setIsValidateClicked] = React.useState<
        boolean | number
    >(false);
    const [isSalesSupportFavored, setIsSalesSupportFavored] = React.useState<
        boolean
    >(false);

    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isModalArchivedOpened, setIsModalArchivedOpened] = React.useState<
        boolean
    >(false);
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [selectedSaleSupport, setSelectedSaleSupport] = React.useState<any[]>(
        []
    );
    const [
        isValidateButtonDisabled,
        setIsValidateButtonDisabled,
    ] = React.useState<boolean>(false);
    const url =
        localShop.length === 1
            ? `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupportToBeAssociate?userId=${userID}&shopId=${localShop[0].id}&franchiseId=${franchiseID}`
            : `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}&type=activated`;

    const ActionColumn = (e: any) => {
        return e.selectedRow.subRows === undefined ? <div></div> : null;
    };

    let arrayOfCustomColumns: customColumnProps[] = [
        {
            indexOFColumn: 6,
            columnName: t("Actions"),
            customJsx: (e: any) => (
                <CustomIconsAssociateModal
                    element={e}
                    isOneShopClicked={
                        selectedShopsRows.filter(
                            (el: any) => el.subRows !== undefined
                        ).length < 2
                    }
                    isSalesSupportFavored={isSalesSupportFavored}
                    setIsSalesSupportFavored={setIsSalesSupportFavored}
                    selectedSaleSupport={selectedSaleSupport}
                />
            ),
        },
    ];

    async function handleAssociateSaleSupportButtonClick() {
        setIsLoading(true);
        let localData: any[] = [];
        let localShopsSelectedRow: any[] = [];
        let archived: Record<string, string> = {};

        if (isValidateButtonDisabled === false) {
            setIsValidateButtonDisabled(true);
            const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport`;

            if (shopsSelectedRow.length > 0) {
                shopsSelectedRow.forEach((element: any) => {
                    element?.archivedSaleSupport?.forEach((el: any) => {
                        archived[el.shopId] = el.Id;
                    });
                    localShopsSelectedRow.push(element.subRows);
                });
            }

            let isReassignment: boolean[] = [];
            let favoriteSelectedSaleSupport: any[] = [];
            let shopSelectedSaleSupport: any[] = [];

            const filtredRows = selectedShopsRows.filter(
                (elt: any) => elt.subRows !== undefined
            );

            for (const element of filtredRows) {
                if (selectedRows.length !== 0) {
                    selectedRows.forEach(() => {
                        favoriteSelectedSaleSupport.push(
                            salesSupportFavored.find(
                                (el: any) => el.status === true
                            )
                        );
                    });

                    if (favoriteSelectedSaleSupport.length > 0) {
                        const shopFavoriteSaleSupport = element.subRows?.find(
                            (el: any) => el.isFavorite === true
                        );

                        if (shopFavoriteSaleSupport) {
                            shopFavoriteSaleSupport.isFavorite = false;
                            const alreadySelected = selectedRows.find(
                                (el: any) =>
                                    el.Id === shopFavoriteSaleSupport.Id
                            );
                            if (!alreadySelected) {
                                selectedRows.push(shopFavoriteSaleSupport);
                                shopSelectedSaleSupport.push(
                                    shopFavoriteSaleSupport
                                );
                            }
                        }
                    }

                    // --- Iterate over each selected sale support ---
                    for (const el of selectedRows) {
                        const indexInAssociated =
                            element.subRows?.findIndex(
                                (item: any) => item.Id === el.Id
                            ) ?? -1;

                        const foundActiveSupport = activeSaleSupports.find(
                            (a: any) => a.Id === el.Id
                        );

                        const isLocalAssociated =
                            (element.subRows?.[indexInAssociated] &&
                                element.subRows[indexInAssociated].isActive !==
                                    foundActiveSupport?.state) ||
                            (element.subRows?.[indexInAssociated] &&
                                element.subRows[indexInAssociated]
                                    .isFavorite !==
                                    foundActiveSupport?.isFavorite);

                        isReassignment.push(!!isLocalAssociated);

                        const isFavorite =
                            salesSupportFavored.find(
                                (fav: any) => fav.id === el.Id
                            )?.status ?? el.isFavorite;

                        const baseData = {
                            id: el.Id,
                            displayName: el[t("Display name")],
                            shopId: element.shopId,
                            support_de_vente: el[t("Sale support")],
                            tag: el.Tag,
                            isActive: foundActiveSupport?.state,
                            isArchived: el.isArchived,
                            isFavorite,
                            advancedDisplayName: el.advancedDisplayName,
                        };

                        const objectData =
                            shopSelectedSaleSupport[0]?.Id === el.Id
                                ? {
                                      userId: userID,
                                      shopId: [element.shopId],
                                      franchiseId: franchiseID,
                                      operatorId: operatorID,
                                      uuidUser: uuidUser,
                                      data: { [el.Id]: baseData },
                                  }
                                : {
                                      affectation: !isLocalAssociated
                                          ? true
                                          : "reassignment",
                                      userId: userID,
                                      shopId: [element.shopId],
                                      franchiseId: franchiseID,
                                      operatorId: operatorID,
                                      uuidUser: uuidUser,
                                      data: { [el.Id]: baseData },
                                  };

                        localData.push(objectData);
                    }
                }
            }

            // --- Perform API call via fetchGlobal ---
            try {
                const data = await fetchGlobal<any>(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(localData),
                });

                if (data.error) throw new Error("Error saving association");
                showSuccessToast(
                    !isReassignment.includes(true)
                        ? `${t("The assignment was successful")}`
                        : `${t("The reassignment was successful")}`
                );

                setKey(uuid());
                setIsValidateClicked(false);
                setIsAssociateModalOpened(!isAssociateModalOpened);
                setSelectedRows([]);
            } catch (e) {
                ErrorToast(`${t("There's an error")!}`);
            } finally {
                setIsValidateButtonDisabled(false);
                setIsLoading(false);
            }
        }
    }

    function CountIds() {
        let number = 0;
        idsLocalShop.forEach((el: any) => {
            selectedRows.forEach((v: any) => {
                if (v.Id === el) {
                    number = number + 1;
                }
            });
        });
        setIds(number);
    }

    function checkIfSaleSupportExistAndArchived() {
        let archived: any[] = [];
        if (shopsSelectedRow.length > 0) {
            shopsSelectedRow.forEach((element: any) => {
                element?.archivedSaleSupports?.forEach((el: any) => {
                    archived.push(el.Id);
                });
            });
        }
        return archived;
    }

    React.useEffect(() => {
        let localSelectedSaleSupport: any = [];
        selectedShopsRows.forEach((el: any) => {
            if (el.subRows !== undefined)
                el.subRows.forEach((element: any) => {
                    localSelectedSaleSupport.push(element);
                });
        });
        setSelectedSaleSupport(localSelectedSaleSupport);
    }, [selectedShopsRows, selectedRows, activeSaleSupports]);

    React.useEffect(() => {
        if (isValidateClicked === true) handleAssociateSaleSupportButtonClick();
        // eslint-disable-next-line
    }, [isValidateClicked]);

    return (
        <React.Fragment>
            <StyledModal
                toggle={true}
                isOpen={isAssociateModalOpened!}
                centered
                className="modal-wrapper__clz"
                backdrop="static"
                fade={false}
                scrollable
            >
                <ModalHeader
                    className="text-capitalize sdv_icn_AFclose"
                    toggle={() => {
                        setIsAssociateModalOpened(!isAssociateModalOpened);
                    }}
                >
                    <StyledH2 className="text-uppercase sdv_txt_AFlistOfSalesSupports">
                        {t("List of sales support")}
                    </StyledH2>
                </ModalHeader>
                <ModalBody>
                    <DynamicTable
                        url={url}
                        requestHeader={{
                            "Accept-Language": i18nextLng,
                            ...getAuthHeader,
                        }}
                        canSelect
                        customSelect
                        canResize
                        setSelectedRows={setSelectedRows}
                        actionColumn={ActionColumn}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        showGlobalFilter
                        canMovedCheckboxLeftOnExpand
                        setDataIsUpdated={
                            setShopDataIsUpdated as
                                | React.Dispatch<
                                      React.SetStateAction<number | boolean>
                                  >
                                | undefined
                        }
                        dataIsUpdated={shopDataIsUpdated}
                        elevationTable={1}
                        minHeight="500px"
                        maxHeight="500px"
                        name="associateModeOfSale"
                    />
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <CustomSecondaryColorButton
                        outline
                        variant="light"
                        onClick={() => {
                            setIsAssociateModalOpened(!isAssociateModalOpened);
                        }}
                        rounded
                        className="sdv_btn_AFcancel"
                    >
                        {t("Cancel")}
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        onClick={() => {
                            let archived = checkIfSaleSupportExistAndArchived();
                            if (selectedRows.length === 0) {
                                ErrorToast(
                                    `${t(
                                        "Attention! You must select at least one sale support"
                                    )!}`
                                );
                            } else if (
                                !selectedRows.some(
                                    (elt: any) =>
                                        JSON.parse(
                                            JSON.stringify(salesSupportFavored)
                                        ).find(
                                            (element: any) =>
                                                element.id === elt.Id
                                        ) !== undefined &&
                                        JSON.parse(
                                            JSON.stringify(salesSupportFavored)
                                        ).find(
                                            (element: any) =>
                                                element.id === elt.Id
                                        ).status === true
                                )
                            ) {
                                ErrorToast(
                                    t(
                                        "Attention, at least one sales support must be favorite"
                                    )
                                );
                            } else {
                                CountIds();
                                try {
                                    setSelectedRow(selectedRows);
                                    selectedRows.forEach((el: any) => {
                                        associatedSaleSupport.forEach(
                                            (element: any) => {
                                                if (
                                                    activeSaleSupports.find(
                                                        (elt: any) =>
                                                            elt.Id === el.Id
                                                    ) !== undefined &&
                                                    element.Id === el.Id &&
                                                    element.isActive !==
                                                        activeSaleSupports.find(
                                                            (elt: any) =>
                                                                elt.Id === el.Id
                                                        ).state
                                                ) {
                                                    setIsModalOpened(
                                                        !isModalOpened
                                                    );
                                                    // eslint-disable-next-line
                                                    throw "Break";
                                                }
                                            }
                                        );
                                    });
                                    selectedRows.forEach((el: any) => {
                                        if (archived.includes(el.Id)) {
                                            setIsModalArchivedOpened(
                                                !isModalArchivedOpened
                                            );
                                            // eslint-disable-next-line
                                            throw "Break";
                                        }
                                    });
                                    handleAssociateSaleSupportButtonClick();
                                } catch (e) {
                                    if (e !== "Break") throw e;
                                }
                            }
                        }}
                        className="sdv_btn_AFvalidate"
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </ModalFooter>
            </StyledModal>
            {isModalOpened ? (
                <ConfirmationMessage
                    msg={t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                    setIsModalOpened={setIsModalOpened}
                    isModalOpened={isModalOpened}
                    setIsValidateClicked={setIsValidateClicked}
                />
            ) : null}
            {isModalArchivedOpened ? (
                <ConfirmationMessage
                    msg={t(
                        "Attention! One or more selected languages are already assigned and archived at the store level.Would you unarchive them?"
                    )}
                    setIsModalOpened={setIsModalArchivedOpened}
                    isModalOpened={isModalArchivedOpened}
                    setIsValidateClicked={setIsValidateClicked}
                />
            ) : null}
        </React.Fragment>
    );
}
